import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, vModelText as _vModelText } from "vue"

const _hoisted_1 = { key: 1 }
const _hoisted_2 = {
  key: 0,
  class: "field is-horizontal"
}
const _hoisted_3 = { class: "field-label" }
const _hoisted_4 = { class: "label" }
const _hoisted_5 = { class: "field-body" }
const _hoisted_6 = { class: "control" }
const _hoisted_7 = { class: "select" }
const _hoisted_8 = ["id"]
const _hoisted_9 = {
  key: 1,
  class: "field is-horizontal"
}
const _hoisted_10 = { class: "field-label" }
const _hoisted_11 = { class: "label" }
const _hoisted_12 = { class: "field-body" }
const _hoisted_13 = { class: "control" }
const _hoisted_14 = {
  key: 2,
  class: "field is-horizontal"
}
const _hoisted_15 = { class: "field-label" }
const _hoisted_16 = { class: "label" }
const _hoisted_17 = { class: "field-body" }
const _hoisted_18 = { class: "control" }
const _hoisted_19 = {
  key: 0,
  class: "help is-danger"
}
const _hoisted_20 = {
  key: 1,
  class: "help is-danger"
}

import { ref, computed, watch, PropType } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required, minLength, requiredIf } from '@vuelidate/validators';
import { StateViewModel } from '@/features/bots/models/designer';
import InputPatternEditor from './InputPatternEditor.vue';
import TransitionPicker from '@/features/bots/components/designer/TransitionPicker.vue';
import { TransitionViewModel } from '@/features/bots/models/designer/transitionViewModel';
import { TransitionType } from '@/features/bots/models/designer/transitionType';
import { useStore } from 'vuex';
import VuelidateUtil from '@shared/util/vuelidateUtil';
import { ConditionOperationType } from '@shared/components/conditions/models/conditions';


export default /*@__PURE__*/_defineComponent({
  __name: 'InputValidationConfig',
  props: {
  value: {
    type: Object as PropType<StateViewModel>,
    required: true,
  },
  fields: {
    type: Array as PropType<string[]>,
    default: [],
  },
},
  emits: ["updated"],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

const store = useStore();
const isTransitionPayloadInvalid = (payload: string) =>
  computed(() => store.getters.isTransitionPayloadInvalid(payload));

const state = ref<StateViewModel>({
  id: '',
  component: 'InputString',
});

const noMatch = ref({});
const allowedAttempts = ref(-1);
const allowsAnyValue = ref(true);
const invalidPattern = ref(false);

const allowedAttemptsId = computed(() => `allowed-attempts-${state.value.id}`);
const isTypeConfigRequired = computed(() => {
  return !isInputForCustomAttribute() || isInputCustomAttributeDataType('text');
});

const validations = {
  state: {
    id: { required },
    component: { required },
    pattern: {
      required: requiredIf(() => isVisible('pattern')),
      minLength: minLength(1),
    },
    noMatch: {
      payload: {
        payloadValid(payload: string) {
          return !payload || !isTransitionPayloadInvalid(payload).value;
        },
      },
    },
  },
};

const $v = useVuelidate(validations, { state });

const isTransitionValidationInvalid = computed(() => VuelidateUtil.isValidationInvalid($v, (v) => v.state.noMatch));
const isTransitionPayloadValidationInvalid = computed(() =>
  VuelidateUtil.isValidationInvalid($v, (v) => v.state.noMatch.payload, 'payloadValid'),
);

watch(
  () => props.value,
  (newValue: StateViewModel) => {
    state.value = newValue;
    refreshComponent();
  },
  { immediate: true },
);

watch(
  () => props.fields,
  () => {
    refreshComponent();
    configChanged();
  },
);

function noMatchUpdated(action: TransitionViewModel) {
  noMatch.value = action;
  state.value.noMatch = action;
  configChanged();
}

function patternUpdated(pattern: string, invalid: boolean) {
  let hasPatternChanged = false;

  if (state.value.pattern !== pattern || invalidPattern.value !== invalid) {
    hasPatternChanged = true;
  }

  state.value.pattern = pattern;
  invalidPattern.value = invalid;
  allowsAnyValue.value = allowsAnyPattern();

  if (allowsAnyValue.value) {
    clearMatchingOptions();
  }

  if (hasPatternChanged) {
    configChanged();
  }
}

function allowedAttemptsUpdated() {
  state.value.allowedAttempts = Number(allowedAttempts.value);
  configChanged();
}

function configChanged() {
  $v.value.$touch();
  emit('updated', state.value, invalidPattern.value || $v.value.$invalid);
}

function isVisible(key: string) {
  return props.fields && props.fields.includes(key);
}

function allowsAnyPattern() {
  return state.value.pattern === '^.+$';
}

function clearMatchingOptions() {
  allowedAttempts.value = -1;
  noMatch.value = '';
  state.value.allowedAttempts = -1;
  state.value.noMatch = { type: TransitionType.Path, payload: '' };
  state.value.errorMessage = '';
}

function refreshComponent() {
  if (state.value.noMatch) {
    noMatch.value = state.value.noMatch;
  }

  if (state.value.allowedAttempts !== null && state.value.allowedAttempts !== undefined) {
    allowedAttempts.value = state.value.allowedAttempts;
  }

  allowsAnyValue.value = allowsAnyPattern();

  if ($v.value.$invalid) {
    configChanged();
  }
}

function isInputForCustomAttribute() {
  return state.value.storedVariable?.scope === 'appUser.customAttributes';
}

function isInputCustomAttributeDataType(dataType: string) {
  return state.value.storedVariable?.dataType === dataType;
}

return (_ctx: any,_cache: any) => {
  const _component_n_tooltip = _resolveComponent("n-tooltip")!

  return (_openBlock(), _createElementBlock("fieldset", null, [
    (isVisible('pattern'))
      ? (_openBlock(), _createBlock(InputPatternEditor, {
          key: 0,
          value: state.value.pattern,
          showType: isTypeConfigRequired.value,
          onUpdated: patternUpdated
        }, null, 8, ["value", "showType"]))
      : _createCommentVNode("", true),
    (!allowsAnyValue.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          (isVisible('allowedAttempts'))
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createElementVNode("div", _hoisted_3, [
                  _createElementVNode("label", _hoisted_4, [
                    _cache[2] || (_cache[2] = _createTextVNode(" Max Attempts ", -1)),
                    _createVNode(_component_n_tooltip, {
                      position: "top",
                      text: "The number of attempts allowed for the inputted value to meet the input requirements. If the limit of attempts is reached, the flow will transition into the 'Max Attempts Exceeded' path"
                    })
                  ])
                ]),
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("div", _hoisted_6, [
                    _createElementVNode("div", _hoisted_7, [
                      _withDirectives(_createElementVNode("select", {
                        id: allowedAttemptsId.value,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((allowedAttempts).value = $event)),
                        onChange: allowedAttemptsUpdated
                      }, [
                        _cache[3] || (_cache[3] = _createElementVNode("option", { value: -1 }, "Unlimited", -1)),
                        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(5, (index) => {
                          return _createElementVNode("option", { key: index }, _toDisplayString(index), 1)
                        }), 64))
                      ], 40, _hoisted_8), [
                        [_vModelSelect, allowedAttempts.value]
                      ])
                    ])
                  ])
                ])
              ]))
            : _createCommentVNode("", true),
          (isVisible('errorMessage') && state.value.allowedAttempts !== 1)
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                _createElementVNode("div", _hoisted_10, [
                  _createElementVNode("label", _hoisted_11, [
                    _cache[4] || (_cache[4] = _createTextVNode(" Error Message ", -1)),
                    _createVNode(_component_n_tooltip, {
                      position: "top",
                      text: "Message to play when a value was entered that doesn\\'t meet the input requirements. If this is blank, the original message will be repeated"
                    })
                  ])
                ]),
                _createElementVNode("div", _hoisted_12, [
                  _createElementVNode("div", _hoisted_13, [
                    _withDirectives(_createElementVNode("textarea", {
                      class: "textarea",
                      placeholder: "Prompt the user to try again",
                      type: "text",
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((state.value.errorMessage) = $event)),
                      onChange: configChanged
                    }, "            ", 544), [
                      [
                        _vModelText,
                        state.value.errorMessage,
                        void 0,
                        { trim: true }
                      ]
                    ])
                  ])
                ])
              ]))
            : _createCommentVNode("", true),
          (isVisible('noMatch') && state.value.allowedAttempts !== -1)
            ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                _createElementVNode("div", _hoisted_15, [
                  _createElementVNode("label", _hoisted_16, [
                    _cache[5] || (_cache[5] = _createTextVNode(" After Max Attempts ", -1)),
                    _createVNode(_component_n_tooltip, {
                      position: "top",
                      text: "Transition to another path if a value was entered that doesn't meet the input requirements"
                    })
                  ])
                ]),
                _createElementVNode("div", _hoisted_17, [
                  _createElementVNode("div", _hoisted_18, [
                    _createVNode(TransitionPicker, {
                      transition: state.value.noMatch,
                      onTransitionSelected: noMatchUpdated
                    }, null, 8, ["transition"]),
                    (isTransitionPayloadValidationInvalid.value)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_19, " Must not contain an invalid transition "))
                      : (isTransitionValidationInvalid.value)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_20, "Must select a transition"))
                        : _createCommentVNode("", true)
                  ])
                ])
              ]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})